import {
  cloneWavetablePreset,
  getWavetableCatalog,
  normalizeWavetablePreset,
  type FilterType,
  type InstrumentSlot,
  type WavetablePreset,
  type WtModRoute,
  type WtWarpMode,
} from "../../../../../src/lib/audio";
import { useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useStoreSelector } from "../../../../../src/lib/store";
import { getLabActions } from "../../labActions";
import { projectStore } from "../../store/projectStore";
import { formatOctaveLabel, MusicalKeyboard } from "../MusicalKeyboard";
import { ParamKnob } from "../ParamKnob";
import { Checkbox } from "../ui/Checkbox";
import { Slider } from "../ui/Slider";
import { instrumentPresetLearnPath } from "../../controls/instrumentPresetLeaves";
import { WavetableBankBar } from "./WavetableBankBar";
import { UnisonVoiceVisualizer } from "./UnisonVoiceVisualizer";

const WARP_MODES: WtWarpMode[] = ["none", "bend", "mirror", "sync"];
const FILTERS: FilterType[] = ["lowpass", "highpass", "bandpass"];
const LFO_WAVES = ["sine", "tri"] as const;
const SUB_WAVES = ["sine", "square"] as const;
const MOD_SOURCES = ["ampEnv", "modEnv", "lfo", "key"] as const;
const MOD_TARGETS = [
  "pitch",
  "cutoff",
  "amp",
  "tablePos1",
  "tablePos2",
  "warp1",
  "warp2",
  "unisonDetune",
  "unisonSpread",
  "unisonEdgeDecay",
  "pmDepth",
  "subLevel",
] as const;

/** Compact dock body for wavetable slots (full authoring lives in WavetableAuthorModal). */
export function WavetableEditor({ slot }: { slot: InstrumentSlot }) {
  const instruments = useStoreSelector(projectStore, s => s.instruments);
  const instrument = instruments[slot];
  const preset = instrument?.kind === "wavetable"
    ? normalizeWavetablePreset(instrument.preset)
    : null;
  const panelRef = useRef<HTMLDivElement>(null);
  const [octaveShift, setOctaveShift] = useState(0);
  const catalog = useMemo(() => getWavetableCatalog(), []);

  useLayoutEffect(() => {
    getLabActions()?.setInstrumentSlot?.(slot);
    setOctaveShift(0);
  }, [slot]);

  if (!preset) {
    return <div className="muted flex min-h-0 flex-1 flex-col overflow-hidden">Not a wavetable slot.</div>;
  }

  function commit(next: WavetablePreset): void {
    getLabActions()?.updateInstrumentWavetablePreset?.(slot, next);
  }

  function patch(partial: Partial<WavetablePreset>): void {
    commit(normalizeWavetablePreset({ ...cloneWavetablePreset(preset!), ...partial }));
  }

  return (
    <div ref={panelRef} className="flex min-h-0 flex-1 flex-col overflow-hidden outline-none" tabIndex={0}>
      <WavetableBankBar slot={slot} />
      <div className="min-h-0 flex-1 overflow-auto px-[0.6rem] py-2 text-[11px]">
        <label className="mb-1 block text-lab-muted">Name</label>
        <input
          className="mb-2 w-full rounded border border-lab-line bg-[#12161c] px-1 py-0.5"
          value={preset.name}
          onChange={e => patch({ name: e.target.value })}
        />

        <Section title="Unison">
          <UnisonVoiceVisualizer
            voices={preset.voices}
            detune={preset.unisonDetune}
            spread={preset.unisonSpread}
            edgeDecay={preset.unisonEdgeDecay}
          />
          <SliderRow label="Voices" path="voices" slot={slot} min={1} max={9} step={1} value={preset.voices} onChange={v => patch({ voices: v })} />
          <SliderRow label="Detune" path="unisonDetune" slot={slot} min={0} max={100} step={0.5} value={preset.unisonDetune} onChange={v => patch({ unisonDetune: v })} />
          <SliderRow label="Spread" path="unisonSpread" slot={slot} min={0} max={1} step={0.01} value={preset.unisonSpread} onChange={v => patch({ unisonSpread: v })} />
          <SliderRow label="Edge" path="unisonEdgeDecay" slot={slot} min={0} max={1} step={0.01} value={preset.unisonEdgeDecay} onChange={v => patch({ unisonEdgeDecay: v })} />
          <SliderRow label="Glide ms" path="glideMs" slot={slot} min={0} max={500} step={1} value={preset.glideMs ?? 0} onChange={v => patch({ glideMs: v })} />
        </Section>

        <OscBlock
          title="Osc 1"
          prefix="osc1"
          slot={slot}
          osc={preset.osc1}
          catalog={catalog}
          onChange={osc1 => patch({ osc1 })}
        />
        <OscBlock
          title="Osc 2"
          prefix="osc2"
          slot={slot}
          osc={preset.osc2}
          catalog={catalog}
          onChange={osc2 => patch({ osc2 })}
        />

        <Section title="Mix / PM">
          <label className="mb-1 flex items-center gap-2 text-lab-muted">
            <Checkbox
              checked={preset.sync}
              aria-label="Sync osc2 to osc1"
              onCheckedChange={sync => patch({ sync })}
            />
            Sync osc2 → osc1
          </label>
          <SliderRow label="PM depth" path="pmDepth" slot={slot} min={0} max={4} step={0.01} value={preset.pmDepth} onChange={v => patch({ pmDepth: v })} />
          <Select
            label="Sub wave"
            value={preset.sub.wave}
            options={SUB_WAVES}
            onChange={wave => patch({ sub: { ...preset.sub, wave: wave as "sine" | "square" } })}
          />
          <SliderRow label="Sub oct" path="sub.octave" slot={slot} min={-2} max={0} step={1} value={preset.sub.octave} onChange={v => patch({ sub: { ...preset.sub, octave: v } })} />
          <SliderRow label="Sub level" path="sub.level" slot={slot} min={0} max={1} step={0.01} value={preset.sub.level} onChange={v => patch({ sub: { ...preset.sub, level: v } })} />
          <SliderRow label="Noise" path="noise.level" slot={slot} min={0} max={1} step={0.01} value={preset.noise.level} onChange={v => patch({ noise: { level: v } })} />
        </Section>

        <Section title="Filter">
          <Select
            label="Type"
            value={preset.filter.type}
            options={FILTERS}
            onChange={type => patch({ filter: { ...preset.filter, type: type as FilterType } })}
          />
          <SliderRow label="Cutoff" path="filter.cutoffHz" slot={slot} min={40} max={12000} step={1} value={preset.filter.cutoffHz} onChange={v => patch({ filter: { ...preset.filter, cutoffHz: v } })} />
          <SliderRow label="Res" path="filter.resonance" slot={slot} min={0} max={1} step={0.01} value={preset.filter.resonance} onChange={v => patch({ filter: { ...preset.filter, resonance: v } })} />
        </Section>

        <Section title="Amp env">
          <SliderRow label="A" path="ampEnv.a" slot={slot} min={0.001} max={2} step={0.001} value={preset.ampEnv.a} onChange={v => patch({ ampEnv: { ...preset.ampEnv, a: v } })} />
          <SliderRow label="D" path="ampEnv.d" slot={slot} min={0.001} max={2} step={0.001} value={preset.ampEnv.d} onChange={v => patch({ ampEnv: { ...preset.ampEnv, d: v } })} />
          <SliderRow label="S" path="ampEnv.s" slot={slot} min={0} max={1} step={0.01} value={preset.ampEnv.s} onChange={v => patch({ ampEnv: { ...preset.ampEnv, s: v } })} />
          <SliderRow label="R" path="ampEnv.r" slot={slot} min={0.001} max={3} step={0.001} value={preset.ampEnv.r} onChange={v => patch({ ampEnv: { ...preset.ampEnv, r: v } })} />
        </Section>

        <Section title="Mod env">
          <SliderRow label="A" path="modEnv.a" slot={slot} min={0.001} max={2} step={0.001} value={preset.modEnv.a} onChange={v => patch({ modEnv: { ...preset.modEnv, a: v } })} />
          <SliderRow label="D" path="modEnv.d" slot={slot} min={0.001} max={2} step={0.001} value={preset.modEnv.d} onChange={v => patch({ modEnv: { ...preset.modEnv, d: v } })} />
          <SliderRow label="S" path="modEnv.s" slot={slot} min={0} max={1} step={0.01} value={preset.modEnv.s} onChange={v => patch({ modEnv: { ...preset.modEnv, s: v } })} />
          <SliderRow label="R" path="modEnv.r" slot={slot} min={0.001} max={3} step={0.001} value={preset.modEnv.r} onChange={v => patch({ modEnv: { ...preset.modEnv, r: v } })} />
        </Section>

        <Section title="LFO">
          <Select
            label="Wave"
            value={preset.lfo.wave}
            options={LFO_WAVES}
            onChange={wave => patch({ lfo: { ...preset.lfo, wave: wave as "sine" | "tri" } })}
          />
          <SliderRow label="Rate Hz" path="lfo.rateHz" slot={slot} min={0.01} max={20} step={0.01} value={preset.lfo.rateHz} onChange={v => patch({ lfo: { ...preset.lfo, rateHz: v } })} />
          <SliderRow label="Depth" path="lfo.depth" slot={slot} min={0} max={1} step={0.01} value={preset.lfo.depth} onChange={v => patch({ lfo: { ...preset.lfo, depth: v } })} />
        </Section>

        <Section title="Mod matrix">
          {preset.routes.map((route, i) => (
            <div
              key={i}
              className="mb-1 grid grid-cols-[1fr_1fr_auto_auto_auto] items-center gap-1"
            >
              <select
                className="rounded border border-lab-line bg-[#12161c]"
                value={route.source}
                onChange={e => {
                  const routes = [...preset.routes];
                  routes[i] = { ...route, source: e.target.value as WtModRoute["source"] };
                  patch({ routes });
                }}
              >
                {MOD_SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <select
                className="rounded border border-lab-line bg-[#12161c]"
                value={route.target}
                onChange={e => {
                  const routes = [...preset.routes];
                  routes[i] = { ...route, target: e.target.value as WtModRoute["target"] };
                  patch({ routes });
                }}
              >
                {MOD_TARGETS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <ParamKnob
                min={-2}
                max={2}
                step={0.01}
                value={route.depth}
                defaultValue={0.3}
                density="compact"
                size={28}
                valuePlacement="end"
                ariaLabel={`Route ${i + 1} depth`}
                learnPath={instrumentPresetLearnPath(slot, `routes.${i}.depth`)}
                onChange={depth => {
                  const routes = [...preset.routes];
                  routes[i] = { ...route, depth };
                  patch({ routes });
                }}
              />
              <label className="flex items-center gap-[0.28rem] text-[10px] text-lab-muted">
                <Checkbox
                  checked={Boolean(route.invert)}
                  aria-label={`Route ${i + 1} invert`}
                  onCheckedChange={invert => {
                    const routes = [...preset.routes];
                    routes[i] = { ...route, invert: invert || undefined };
                    patch({ routes });
                  }}
                />
                inv
              </label>
              <button
                type="button"
                className="px-1"
                title="Remove"
                onClick={() => patch({ routes: preset.routes.filter((_, j) => j !== i) })}
              >
                ×
              </button>
            </div>
          ))}
          <button
            type="button"
            className="mt-1 text-lab-accent"
            onClick={() =>
              patch({
                routes: [...preset.routes, { source: "modEnv", target: "cutoff", depth: 0.3 }],
              })
            }
          >
            + route
          </button>
        </Section>
      </div>

      <div className="border-t border-lab-line px-2 py-1">
        <div className="mb-1 flex items-center justify-between text-[10px] text-lab-muted">
          <span>Keyboard</span>
          <span>{formatOctaveLabel(octaveShift)}</span>
        </div>
        <MusicalKeyboard
          key={slot}
          focusHostRef={panelRef}
          baseMidi={48}
          octaves={2}
          onNoteOn={(note, vel) => getLabActions()?.instrumentNoteOn?.(slot, note, vel)}
          onNoteOff={note => getLabActions()?.instrumentNoteOff?.(slot, note)}
          onOctaveChange={setOctaveShift}
        />
      </div>
    </div>
  );
}

function OscBlock({
  title,
  prefix,
  slot,
  osc,
  catalog,
  onChange,
}: {
  title: string;
  prefix: "osc1" | "osc2";
  slot: InstrumentSlot;
  osc: WavetablePreset["osc1"];
  catalog: ReturnType<typeof getWavetableCatalog>;
  onChange: (osc: WavetablePreset["osc1"]) => void;
}) {
  const groups = useMemo(() => {
    const map = new Map<string, typeof catalog>();
    for (const entry of catalog) {
      const g = entry.group ?? "Other";
      const list = map.get(g) ?? [];
      list.push(entry);
      map.set(g, list);
    }
    return [...map.entries()];
  }, [catalog]);

  return (
    <Section title={title}>
      <label className="mb-1 block text-lab-muted">Table</label>
      <select
        className="mb-1 w-full rounded border border-lab-line bg-[#12161c]"
        value={osc.tableId}
        onChange={e => onChange({ ...osc, tableId: e.target.value })}
      >
        {groups.map(([group, entries]) => (
          <optgroup key={group} label={group}>
            {entries.map(entry => (
              <option key={entry.id} value={entry.id}>{entry.name}</option>
            ))}
          </optgroup>
        ))}
      </select>
      <SliderRow label="Position" path={`${prefix}.position`} slot={slot} min={0} max={1} step={0.001} value={osc.position} onChange={v => onChange({ ...osc, position: v })} />
      <SliderRow label="Warp" path={`${prefix}.warp`} slot={slot} min={0} max={1} step={0.01} value={osc.warp} onChange={v => onChange({ ...osc, warp: v })} />
      <Select
        label="Warp mode"
        value={osc.warpMode}
        options={WARP_MODES}
        onChange={mode => onChange({ ...osc, warpMode: mode as WtWarpMode })}
      />
      <SliderRow label="Octave" path={`${prefix}.octave`} slot={slot} min={-3} max={3} step={1} value={osc.octave} onChange={v => onChange({ ...osc, octave: v })} />
      <SliderRow label="Semis" path={`${prefix}.semis`} slot={slot} min={-12} max={12} step={1} value={osc.semis} onChange={v => onChange({ ...osc, semis: v })} />
      <SliderRow label="Detune" path={`${prefix}.detuneCents`} slot={slot} min={-100} max={100} step={1} value={osc.detuneCents} onChange={v => onChange({ ...osc, detuneCents: v })} />
      <SliderRow label="Level" path={`${prefix}.level`} slot={slot} min={0} max={1} step={0.01} value={osc.level} onChange={v => onChange({ ...osc, level: v })} />
    </Section>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mt-2 border-t border-lab-line pt-2 first:mt-0 first:border-t-0 first:pt-0">
      <div className="mb-1 font-medium text-slate-200">{title}</div>
      {children}
    </div>
  );
}

function Select({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: readonly string[];
  onChange: (value: string) => void;
}) {
  return (
    <label className="mb-1 block text-lab-muted">
      <span className="mb-0.5 block">{label}</span>
      <select
        className="w-full rounded border border-lab-line bg-[#12161c]"
        value={value}
        onChange={e => onChange(e.target.value)}
      >
        {options.map(opt => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    </label>
  );
}

function SliderRow({
  label,
  path,
  slot,
  min,
  max,
  step,
  value,
  onChange,
}: {
  label: string;
  path: string;
  slot: InstrumentSlot;
  min: number;
  max: number;
  step: number;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <label className="mb-0.5 grid grid-cols-[4.5rem_1fr_2.2rem] items-center gap-1 text-lab-muted">
      <span>{label}</span>
      <Slider
        min={min}
        max={max}
        step={step}
        value={value}
        data-path={path}
        aria-label={label}
        learnPath={instrumentPresetLearnPath(slot, path)}
        onChange={onChange}
      />
      <span className="text-right tabular-nums text-[10px] text-slate-300">{value}</span>
    </label>
  );
}
